/**
 * PoseCheckerV2.ts — vertical-angle-space pose validator.
 *
 * Replaces the MediaPipe-atan2 heuristics in PoseChecker.ts with checks
 * that work in "degrees from vertical-down" space, matching the backend
 * rembg/pose_config.py and the training distribution in
 * sampling_config_512_perview.yaml.
 *
 * Coordinate system expected for `landmarks`:
 *   poseFinalWorld from MediaPipe.tsx — normalized MediaPipe 0-1 coords
 *   scaled to video pixel dimensions (width × height).
 *   y increases downward, x increases rightward.
 *   MIRROR flip is already applied by calculateAveragePose, so
 *   keys.rightShoulder always refers to the anatomical right side.
 *
 * Fixes vs PoseChecker.ts:
 *   • Angles are in vertical-angle space (mirror-invariant |dx|).
 *   • Front arm acceptance [15°, 50°] vs legacy [20°, 40°] — covers inlier
 *     p5–p95 (26.6°–45°) and stops rejecting valid users.
 *   • isRightLegLower bug fixed: lower-leg check uses rightLeg, not leftLeg.
 *   • Side pose adds geometric arm-inside-body check (dx_wrist/trunk).
 *   • Side pose no longer fails on far-side (left) low-visibility keypoints.
 *   • Torso tilt is validated (was absent in legacy code).
 *
 * Connection index → limb mapping (matches PoseChecker.ts connections array):
 *   0  rightShoulder → rightElbow   (right upper arm)
 *   1  rightElbow    → rightWrist   (right lower arm / elbow bend)
 *   2  leftShoulder  → leftElbow    (left upper arm)
 *   3  leftElbow     → leftWrist    (left lower arm / elbow bend)
 *   4  rightHip      → rightKnee   (right upper leg)
 *   5  rightKnee     → rightAnkle  (right lower leg)
 *   6  leftHip       → leftKnee    (left upper leg)
 *   7  leftKnee      → leftAnkle   (left lower leg)
 */

import type { NormalizedLandmark } from "@mediapipe/tasks-vision";
import { POSE_GATE } from "./poseGate.generated";
import { keys, toIndex } from "./PoseChecker";

type Point = Pick<NormalizedLandmark, "x" | "y" | "visibility">;

// Front view: ankle distance relative to hip width. If feet are too close,
// this ratio drops and should fail the pose.
const FRONT_FEET_MIN_RATIO = 0.75;

// ---------------------------------------------------------------------------
// Geometry primitives
// ---------------------------------------------------------------------------

/**
 * Angle in degrees [0, 180] from straight-down vertical.
 * 0° = limb points straight down, 90° = horizontal, 180° = straight up.
 * Mirror-invariant: abs(dx) is used so left/right arms give the same reading.
 */
function angleFromVertical(start: Point, end: Point): number {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  // atan2(|dx|, dy): positive when dy > 0 (pointing down), negative when dy < 0.
  // Full range via atan2 semantics: [0°, 180°].
  return Math.atan2(Math.abs(dx), dy) * (180 / Math.PI);
}

/**
 * Interior angle at the elbow joint in degrees [0, 180].
 * 180° = fully straight arm.
 */
function elbowJointAngle(shoulder: Point, elbow: Point, wrist: Point): number {
  const v1x = shoulder.x - elbow.x;
  const v1y = shoulder.y - elbow.y;
  const v2x = wrist.x - elbow.x;
  const v2y = wrist.y - elbow.y;
  const dot = v1x * v2x + v1y * v2y;
  const len =
    Math.sqrt(v1x * v1x + v1y * v1y) * Math.sqrt(v2x * v2x + v2y * v2y);
  if (len < 1e-6) return 180;
  return Math.acos(Math.max(-1, Math.min(1, dot / len))) * (180 / Math.PI);
}

/**
 * Lateral tilt of the torso from vertical-down, degrees in [0, 180].
 * 0° = upright, grows when the body leans sideways.
 */
function torsoTiltAngle(
  ls: Point,
  rs: Point,
  lh: Point,
  rh: Point
): number {
  const midShoulder = { x: (ls.x + rs.x) / 2, y: (ls.y + rs.y) / 2, visibility: 1 };
  const midHip = { x: (lh.x + rh.x) / 2, y: (lh.y + rh.y) / 2, visibility: 1 };
  return angleFromVertical(midShoulder, midHip);
}

/**
 * Normalised horizontal offset of the elbow and wrist relative to the
 * shoulder-hip trunk segment midpoint.  Denominator is the 2-D Euclidean
 * trunk length so the ratio is dimensionless (pixel-aspect-safe).
 *
 * Used for the side-pose "arm tucked inside body silhouette" check.
 * Dataset inlier p95: dxWrist ≈ 0.22, dxElbow ≈ 0.15.
 */
function armInsideBodyDx(
  shoulder: Point,
  hip: Point,
  elbow: Point,
  wrist: Point
): { dxElbow: number; dxWrist: number } {
  const trunkLen = Math.sqrt(
    (shoulder.x - hip.x) ** 2 + (shoulder.y - hip.y) ** 2
  );
  if (trunkLen < 1e-6) return { dxElbow: 0, dxWrist: 0 };
  const trunkX = (shoulder.x + hip.x) / 2;
  return {
    dxElbow: Math.abs(elbow.x - trunkX) / trunkLen,
    dxWrist: Math.abs(wrist.x - trunkX) / trunkLen,
  };
}

// ---------------------------------------------------------------------------
// Landmark accessor (compressed array, same indexing as PoseChecker.ts)
// ---------------------------------------------------------------------------

function lm(landmarks: Point[], key: number): Point {
  return landmarks[toIndex(key)];
}

function inRange([lo, hi]: readonly [number, number], v: number): boolean {
  return v >= lo && v <= hi;
}

// ---------------------------------------------------------------------------
// Per-check result type
// true = check PASSES (no error)
// ---------------------------------------------------------------------------

export interface CheckResults {
  /** Arm angle from vertical within acceptance range */
  rArmAngle: boolean;
  lArmAngle: boolean;
  /** Elbow is sufficiently straight (>= elbow.min degrees) */
  rElbow: boolean;
  lElbow: boolean;
  /** Leg angle from vertical within acceptance range */
  rLegAngle: boolean;
  lLegAngle: boolean;
  /** Front-pose feet spacing: ankle width relative to hip width */
  feetWidth: boolean;
  /** Torso is not tilted beyond acceptance */
  torso: boolean;
  /** Side-pose: wrist/elbow stays within body silhouette (front always true) */
  armInside: boolean;
}

function allOk(c: CheckResults): boolean {
  return (
    c.rArmAngle &&
    c.lArmAngle &&
    c.rElbow &&
    c.lElbow &&
    c.rLegAngle &&
    c.lLegAngle &&
    c.feetWidth &&
    c.torso &&
    c.armInside
  );
}

// ---------------------------------------------------------------------------
// Front-pose check
// ---------------------------------------------------------------------------

export function checkFrontPoseV2(landmarks: Point[]): CheckResults {
  const cfg = POSE_GATE.front;

  const rSh  = lm(landmarks, keys.rightShoulder);
  const lSh  = lm(landmarks, keys.leftShoulder);
  const rEl  = lm(landmarks, keys.rightElbow);
  const lEl  = lm(landmarks, keys.leftElbow);
  const rWr  = lm(landmarks, keys.rightWrist);
  const lWr  = lm(landmarks, keys.leftWrist);
  const rHip = lm(landmarks, keys.rightHip);
  const lHip = lm(landmarks, keys.leftHip);
  const rKnee = lm(landmarks, keys.rightKnee);
  const lKnee = lm(landmarks, keys.leftKnee);
  const rAnk = lm(landmarks, keys.rightAnkle);
  const lAnk = lm(landmarks, keys.leftAnkle);

  const hipW = Math.abs(rHip.x - lHip.x);
  const ankW = Math.abs(rAnk.x - lAnk.x);
  const feetRatio = hipW > 1e-6 ? ankW / hipW : 1;

  return {
    rArmAngle: inRange(cfg.arm.accept, angleFromVertical(rSh, rEl)),
    lArmAngle: inRange(cfg.arm.accept, angleFromVertical(lSh, lEl)),
    rElbow:    elbowJointAngle(rSh, rEl, rWr) >= cfg.elbow.min,
    lElbow:    elbowJointAngle(lSh, lEl, lWr) >= cfg.elbow.min,
    rLegAngle: inRange(cfg.leg.accept, angleFromVertical(rHip, rKnee)),
    lLegAngle: inRange(cfg.leg.accept, angleFromVertical(lHip, lKnee)),
    feetWidth: feetRatio >= FRONT_FEET_MIN_RATIO,
    torso:     torsoTiltAngle(lSh, rSh, lHip, rHip) <= cfg.torsoTilt.accept[1],
    armInside: true, // not applicable for front pose
  };
}

// ---------------------------------------------------------------------------
// Side-pose check (right side of person faces camera).
// Pass nearSide="left" when the left side faces the camera (left-view scan).
// Far-side keypoints are not validated — MediaPipe reports them at
// low visibility (~0.2-0.4) and failing on them causes spurious red frames.
// ---------------------------------------------------------------------------

export function checkSidePoseV2(landmarks: Point[], nearSide: "right" | "left" = "right"): CheckResults {
  const cfg = POSE_GATE.side;

  const rSh  = lm(landmarks, keys.rightShoulder);
  const lSh  = lm(landmarks, keys.leftShoulder);
  const rHip = lm(landmarks, keys.rightHip);
  const lHip = lm(landmarks, keys.leftHip);

  // Near-side (visible) landmarks depend on which side faces the camera
  const nSh   = nearSide === "right" ? rSh  : lSh;
  const nEl   = nearSide === "right" ? lm(landmarks, keys.rightElbow)  : lm(landmarks, keys.leftElbow);
  const nWr   = nearSide === "right" ? lm(landmarks, keys.rightWrist)  : lm(landmarks, keys.leftWrist);
  const nHip  = nearSide === "right" ? rHip : lHip;
  const nKnee = nearSide === "right" ? lm(landmarks, keys.rightKnee)   : lm(landmarks, keys.leftKnee);

  const dx = armInsideBodyDx(nSh, nHip, nEl, nWr);
  const nearAngle  = inRange(cfg.arm.accept, angleFromVertical(nSh, nEl));
  const nearElbow  = elbowJointAngle(nSh, nEl, nWr) >= cfg.elbow.min;
  const nearLeg    = inRange(cfg.leg.accept, angleFromVertical(nHip, nKnee));

  return {
    // Store results in the field matching the near side so callers can use
    // semantic names (rArmAngle = right arm, lArmAngle = left arm).
    rArmAngle: nearSide === "right" ? nearAngle : true, // far side not checked
    lArmAngle: nearSide === "left"  ? nearAngle : true, // far side not checked
    rElbow:    nearSide === "right" ? nearElbow : true,
    lElbow:    nearSide === "left"  ? nearElbow : true,
    rLegAngle: nearSide === "right" ? nearLeg   : true,
    lLegAngle: nearSide === "left"  ? nearLeg   : true,
    feetWidth: true, // not checked on side view
    torso:     torsoTiltAngle(lSh, rSh, lHip, rHip) <= cfg.torsoTilt.accept[1],
    armInside:
      dx.dxWrist <= cfg.armInsideBody.maxWristOverTrunk &&
      dx.dxElbow <= cfg.armInsideBody.maxElbowOverTrunk,
  };
}

// ---------------------------------------------------------------------------
// isConnectionErrorV2 — drop-in for isErrorLine in MediaPipe.tsx
// Returns true when the segment should be drawn RED.
// Note: mirrored/MIRROR is NOT needed here because calculateAveragePose
// already applies the coordinate flip before we receive the landmarks.
// ---------------------------------------------------------------------------

export function isConnectionErrorV2(
  landmarks: Point[],
  connectionIndex: number | undefined,
  pose: "front" | "right",
  nearSide: "right" | "left" = "right"
): boolean {
  if (connectionIndex === undefined) return false;

  const checks =
    pose === "right"
      ? checkSidePoseV2(landmarks, nearSide)
      : checkFrontPoseV2(landmarks);

  if (pose === "right" && nearSide === "left") {
    // Left view: near arm = left (connections 2,3), near leg = left (connections 6,7)
    switch (connectionIndex) {
      case 0: return false; // right upper arm — far side
      case 1: return false; // right lower arm — far side
      case 2: return !(checks.lArmAngle && checks.armInside);
      case 3: return !(checks.lElbow    && checks.armInside);
      case 4: return false; // right upper leg — far side
      case 5: return false; // right lower leg — far side
      case 6: return !(checks.lLegAngle && checks.feetWidth);
      case 7: return !(checks.lLegAngle && checks.feetWidth);
      default: return false;
    }
  }

  switch (connectionIndex) {
    // Right arm: arm angle + inside-body check colour both upper and lower segments
    case 0: return !(checks.rArmAngle && checks.armInside);
    case 1: return !(checks.rElbow    && checks.armInside);
    // Left arm
    case 2: return !checks.lArmAngle;
    case 3: return !checks.lElbow;
    // Right leg — both upper and lower segments reflect leg angle
    case 4: return !(checks.rLegAngle && checks.feetWidth);
    case 5: return !(checks.rLegAngle && checks.feetWidth);
    // Left leg
    case 6: return !(checks.lLegAngle && checks.feetWidth);
    case 7: return !(checks.lLegAngle && checks.feetWidth);
    default: return false;
  }
}

/**
 * Returns true when the pose as a whole satisfies all checks.
 * Replaces the `allConnectionsOK` reduction in MediaPipe.tsx.
 */
export function isPoseOKV2(
  landmarks: Point[],
  pose: "front" | "right",
  nearSide: "right" | "left" = "right"
): boolean {
  const checks =
    pose === "right"
      ? checkSidePoseV2(landmarks, nearSide)
      : checkFrontPoseV2(landmarks);
  return allOk(checks);
}

/**
 * Returns a human-readable hint for the first failing check, or null if OK.
 * Designed for future on-canvas overlay (plan §4.4).
 */
export function getPoseHintV2(
  landmarks: Point[],
  pose: "front" | "right",
  nearSide: "right" | "left" = "right",
  isBack = false
): string | null {
  const checks =
    pose === "right"
      ? checkSidePoseV2(landmarks, nearSide)
      : checkFrontPoseV2(landmarks);

  if (!checks.torso)     return "Stand straighter";
  if (!checks.feetWidth) return "Stand with feet shoulder-width apart";
  if (!checks.armInside) return "Tuck your arm in";
  if (!checks.rArmAngle) return isBack ? "Raise your arms" : pose === "front" ? "Raise your right arm" : "Lower your arm";
  if (!checks.lArmAngle) return isBack ? "Raise your arms" : pose === "front" ? "Raise your left arm"  : "Lower your arm";
  if (!checks.rElbow || !checks.lElbow) return "Straighten your elbows";
  if (!checks.rLegAngle || !checks.lLegAngle) return "Stand with feet together";
  return null;
}
